"use client";
import {
  NumericInput,
  RadioButton,
  type RadioItem,
  Typography,
} from "@kyan/ui";

import { EstimatedExecPrice } from "#/components/EstimatedExecPrice/EstimatedExecPrice";
import { TradeFee } from "#/components/TradeFee/TradeFee";
import { tooltips } from "#/config/tooltips";
import { MarketOrder } from "#/types/orders";

import { FocusableSizeInput } from "../../../../../../../FocusableSizeInput/FocusableSizeInput";
import { LimitPriceInputTooltip } from "./components/LimitPriceInputTooltip/LimitPriceInputTooltip";
import { useOptionMarketOrderController } from "./OptionMarketOrder.controller";

const getMarketSubOrderTypes = (
  isOptionHedged: boolean,
): RadioItem<MarketOrder>[] => [
  {
    id: MarketOrder.IOC,
    label: "IOC",
    className: "px-4",
    tooltip: isOptionHedged
      ? "Remove hedge to enable IOC orders"
      : tooltips.IOC,
    disabled: isOptionHedged,
  },
  {
    id: MarketOrder.FOK,
    label: "FOK",
    className: "px-4",
    tooltip: tooltips.FOK,
  },
];

export const OptionMarketOrder: React.FC = () => {
  const {
    isSm,
    sizeError,
    isBuySide,
    optionSize,
    liquidityInfo,
    isOptionHedged,
    liquidityError,
    limitPriceError,
    optionLimitPrice,
    sizeDisplayPrecision,
    optionMarketOrderType,
    priceDisplayPrecisionNotional,
    setOptionLimitPrice,
    setOptionSize,
    handleToggleSubOrderType,
  } = useOptionMarketOrderController();

  return (
    <div className="flex w-full flex-col gap-2">
      <FocusableSizeInput
        inputProps={{
          rightIcon: "Qty",
          className: "w-full",
          value: optionSize,
          extraInfo: liquidityInfo,
          label: "Size in contracts",
          rightLabel: "Equity: 500 USDC",
          error: sizeError || liquidityError,
          autoNumericOptions: {
            decimalPlaces: sizeDisplayPrecision,
          },
          onChange: setOptionSize,
        }}
      />
      <NumericInput
        rightIcon="USD"
        placeholder="$0.00"
        variant="secondary"
        error={limitPriceError}
        value={optionLimitPrice}
        className="ml-auto w-full"
        rightLabel="Equity: 500 USDC"
        wrapperClassName="h-11 sm:h-8"
        autoNumericOptions={{
          currencySymbol: "$",
          decimalPlaces: priceDisplayPrecisionNotional,
        }}
        label={
          <LimitPriceInputTooltip
            label={isBuySide ? "Max Price" : "Min Price"}
          />
        }
        onChange={setOptionLimitPrice}
      />
      <EstimatedExecPrice type="market" kind="options" />
      <TradeFee instrumentType="options" />
      <div className="border-divider mt-[10px] flex items-center justify-between border-b pb-2">
        <Typography grotesk primary size={12}>
          Market-Limit order type
        </Typography>

        <RadioButton
          variant="tertiary"
          size={isSm ? "sm" : "md"}
          value={optionMarketOrderType}
          items={getMarketSubOrderTypes(isOptionHedged)}
          onChange={handleToggleSubOrderType}
        />
      </div>
    </div>
  );
};
